import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import React from 'react';

const ASSETS = 'https://media.easy-peasy.ai/assets';

type ShowcaseBlock = {
  id: string;
  eyebrow: string;
  title: string;
  description: string;
  bullets: string[];
  image: string;
  cta: string;
  href: string;
};

const BLOCKS: ShowcaseBlock[] = [
  {
    id: 'websites',
    eyebrow: 'Web development',
    title: 'Build & deploy websites from chat',
    description:
      'Describe the site you need. Marky scaffolds it, wires up a database, deploys it to a live URL — and iterates with you in plain language.',
    bullets: [
      'Live preview while it codes',
      'Databases, auth and custom domains',
      'Checkpoints you can roll back to',
    ],
    image: `${ASSETS}/feature-build-websites.jpg?v=1`,
    cta: 'Build a site',
    href: '/marky/website-builder',
  },
  {
    id: 'browse',
    eyebrow: 'Browsing & research',
    title: 'A real browser, driven by AI',
    description:
      'Marky opens pages, clicks, fills forms and reads results — and when a login or checkout needs you, it hands over the browser live.',
    bullets: [
      'Deep research with cited sources',
      'Live view — take over anytime',
      'Logins persist across tasks',
    ],
    image: `${ASSETS}/feature-browse-research.jpg?v=1`,
    cta: 'Research something',
    href: '/login?preset=chat',
  },
  {
    id: 'code',
    eyebrow: 'Code & data',
    title: 'Run code, crunch data, chart it',
    description:
      'Drop in a spreadsheet or ask a question — Marky writes and runs the code, then turns the answer into interactive charts.',
    bullets: [
      'Python in a real sandbox',
      'Interactive, exportable charts',
      'Works on your uploaded files',
    ],
    image: `${ASSETS}/feature-code-analyze.jpg?v=1`,
    cta: 'Analyze my data',
    href: '/login?preset=chat',
  },
  {
    id: 'create',
    eyebrow: 'Creation',
    title: 'Slides, images, video & music',
    description:
      'Ask for a pitch deck, a product shot, a cinematic clip or a theme song — Marky calls the best generation model for each and delivers finished files.',
    bullets: [
      'Designed slide decks, PPTX export',
      'Images and video with top models',
      'Songs and lifelike voiceovers',
    ],
    image: `${ASSETS}/feature-create-media.jpg?v=1`,
    cta: 'Create something',
    href: '/login?preset=chat',
  },
];

const MarkyShowcase = () => {
  const { t } = useLocale('marky');

  return (
    <section
      id='features'
      className='relative bg-white py-20 dark:bg-slate-950 lg:py-28'
    >
      <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
        <div className='mb-16 text-center'>
          <h2 className='text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white md:text-5xl'>
            {t('See it in action')}
          </h2>
        </div>

        <div className='space-y-20 lg:space-y-28'>
          {BLOCKS.map((block, index) => (
            <div
              key={block.id}
              id={block.id}
              className='grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16'
            >
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className='text-sm font-bold uppercase tracking-widest text-indigo-600 dark:text-indigo-300'>
                  {t(block.eyebrow)}
                </div>
                <h3 className='mt-3 text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white sm:text-4xl'>
                  {t(block.title)}
                </h3>
                <p className='mt-4 text-lg leading-relaxed text-slate-600 dark:text-slate-400'>
                  {t(block.description)}
                </p>
                <ul className='mt-6 space-y-3'>
                  {block.bullets.map((bullet) => (
                    <li
                      key={bullet}
                      className='flex items-center text-slate-700 dark:text-slate-300'
                    >
                      <svg
                        className='mr-3 h-5 w-5 shrink-0 text-emerald-500 dark:text-emerald-400'
                        fill='none'
                        viewBox='0 0 24 24'
                        stroke='currentColor'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M5 13l4 4L19 7'
                        />
                      </svg>
                      {t(bullet)}
                    </li>
                  ))}
                </ul>
                <Link
                  href={block.href}
                  className='mt-8 inline-flex items-center gap-2 font-bold text-indigo-600 transition-colors hover:text-indigo-500 dark:text-indigo-300 dark:hover:text-indigo-200'
                >
                  {t(block.cta)} <span aria-hidden='true'>→</span>
                </Link>
              </div>
              <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                <div className='relative'>
                  <div
                    aria-hidden='true'
                    className='pointer-events-none absolute -inset-6 rounded-[2.5rem] bg-gradient-to-br from-indigo-500/10 to-fuchsia-500/10 blur-xl'
                  />
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={block.image}
                    alt={t(block.title)}
                    loading='lazy'
                    decoding='async'
                    className='relative w-full rounded-3xl border border-slate-200 shadow-xl ring-1 ring-slate-900/5 dark:border-white/10 dark:ring-white/10'
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MarkyShowcase;
